import * as THREE from 'three';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { Sky } from 'three/examples/jsm/objects/Sky.js';

let camera;
let scene;
let renderer;
let dirLight, hemiLight;
let sky, sun;
let model;
let activeTextureRequestId = 0;
let textureLoadPendingCount = 0;
let loadingOverlay = null;
let latestTileImage = null;
let activeVisualizerId = 0;
let renderFrameId = null;

const MAX_PIXEL_RATIO = 2;
const SHADOW_MAP_SIZE = 2048;
const LOCAL_TEXTURE_PATH = '/day-night-visualizer/tex/';
const MODEL_CACHE_BUSTER = '3.2.2';
// Color UIDs remain stable when a CMS tile image is re-uploaded with a different filename.
const DESIGNER_RIDGE_TEXTURE_FILES = {
  'black-sable': 'DSDP_BlackSable_RGB_tile_lg_07-2022-ridge.jpg',
  'summer-harvest': 'DSDP_SummerHarvest_RGB_tile_lg_07-2022-ridge.jpg',
  bourbon: 'DSDP_Bourbon_RGB_tile_lg_07-2022-ridge.jpg',
  'evergreen-mist': 'TDD_Evergreen_Mist_RGB_tile_lg-ridge.jpg',
  'gray-tweed': 'TDD_Gray_Tweed_RGB_tile_lg-ridge.jpg',
  'mountain-pine': 'TDD_Mountain_Pine_RGB_tile_lg-ridge.jpg',
  'aged-copper': 'TDDesigner_AgedCopper_RGB_Digital_tile-l1-ridge.jpg',
  merlot: 'TDDesigner_Merlot_RGB-tile-l-ridge.jpg',
  'pacific-wave': 'TDDesigner_PacificWave_RGB_Digital_tile-l-ridge.jpg',
  'sand-dune': 'TDDesigner_SandDune_RGB_Digital_tile-l-ridge.jpg',
  'sedona-canyon': 'TDDesigner_SedonaCanyon_RGB_Digital_tile-l-ridge.jpg',
  'storm-cloud': 'TDDesigner_StormCloud_RGB_Digital_tile-l-ridge.jpg',
};

export const SUN_ANGLE = { dusk: 0.7, midday: 1.65, dawn: 2.6 };

const loader = new THREE.TextureLoader();

function hasWebGL2Support() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;

  try {
    const canvas = document.createElement('canvas');
    return Boolean(window.WebGL2RenderingContext && canvas.getContext('webgl2'));
  } catch {
    return false;
  }
}

function setupRenderer(targetContainer) {
  renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO));
  renderer.setSize(targetContainer.getBoundingClientRect().width, targetContainer.getBoundingClientRect().height);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  targetContainer.replaceChildren();
  targetContainer.appendChild(renderer.domElement);
}

function setupScene() {
  scene = new THREE.Scene();
}

function setupCamera() {
  camera = new THREE.PerspectiveCamera(30, window.innerWidth / window.innerHeight, 1, 5000);
  camera.position.set(-8, 0, -4);
  camera.lookAt(scene.position);
}

function isInitialized() {
  return Boolean(renderer && scene && camera && dirLight && sky && sun);
}

export function render() {
  if (!isInitialized()) return;
  renderer.render(scene, camera);
}

function requestRender() {
  if (renderFrameId !== null) return;

  if (typeof window.requestAnimationFrame !== 'function') {
    render();
    return;
  }

  renderFrameId = window.requestAnimationFrame(() => {
    renderFrameId = null;
    render();
  });
}

function disposeScene(targetScene) {
  targetScene.traverse(object => {
    if (!(object instanceof THREE.Mesh)) return;

    object.geometry?.dispose();
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    materials.forEach(material => {
      material.map?.dispose();
      material.dispose();
    });
  });
}

function setupLights() {
  hemiLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 0.05);
  hemiLight.color.setHSL(0.6, 1, 0.6);
  hemiLight.groundColor.setHSL(0.095, 1, 0.75);
  hemiLight.position.set(0, 500, 0);
  scene.add(hemiLight);

  dirLight = new THREE.DirectionalLight(0xffffff, 1);
  dirLight.position.set(-1, 0.75, 1);
  dirLight.position.multiplyScalar(50);
  dirLight.castShadow = true;

  const d = 30;
  dirLight.shadow.camera.left = -d;
  dirLight.shadow.camera.right = d;
  dirLight.shadow.camera.top = d;
  dirLight.shadow.camera.bottom = -d;
  dirLight.shadow.camera.far = 2600;
  dirLight.shadow.bias = -0.0000004;
  dirLight.shadow.mapSize.width = SHADOW_MAP_SIZE;
  dirLight.shadow.mapSize.height = SHADOW_MAP_SIZE;

  scene.add(dirLight);
}

function setupSky() {
  sky = new Sky();
  sky.scale.setScalar(450000);
  scene.add(sky);

  sun = new THREE.Vector3();

  const effectController = {
    turbidity: 10,
    rayleigh: 0.1,
    mieCoefficient: 0.005,
    mieDirectionalG: 0.7,
    elevation: 2,
    azimuth: 180,
    exposure: renderer.toneMappingExposure
  };

  const uniforms = sky.material.uniforms;
  uniforms['turbidity'].value = effectController.turbidity;
  uniforms['rayleigh'].value = effectController.rayleigh;
  uniforms['mieCoefficient'].value = effectController.mieCoefficient;
  uniforms['mieDirectionalG'].value = effectController.mieDirectionalG;

  const phi = THREE.MathUtils.degToRad(90 - effectController.elevation);
  const theta = THREE.MathUtils.degToRad(effectController.azimuth);

  sun.setFromSphericalCoords(1, phi, theta);

  uniforms['sunPosition'].value.copy(dirLight.position);

  renderer.toneMappingExposure = effectController.exposure;
  renderer.render(scene, camera);
}

function loadModels(callBack, visualizerId) {
  new MTLLoader()
    .setPath('/day-night-visualizer/models/')
    .setResourcePath(LOCAL_TEXTURE_PATH)
    .load(`house.mtl?v=${MODEL_CACHE_BUSTER}`, materials => {
      if (visualizerId !== activeVisualizerId) return;
      materials.preload();
      new OBJLoader()
        .setMaterials(materials)
        .setPath('/day-night-visualizer/models/')
        .load(`house.obj?v=${MODEL_CACHE_BUSTER}`, object => {
          if (visualizerId !== activeVisualizerId) return;
          model = object;
          object.scale.setScalar(0.3);
          object.position.y = -2;
          object.rotation.y = -Math.PI / 2;
          model.traverse(mesh => {
            if (mesh instanceof THREE.Mesh) {
              mesh.castShadow = true;
              mesh.receiveShadow = true;

              const split = mesh.name.split('_');
              const meshName = split.length ? split[0] : mesh.name;

              switch (meshName) {

                case 'walls':
                  mesh.material.color.setHex(0xDFDFDF);
                  break;

                case 'slat':
                  mesh.castShadow = false;
                  mesh.material.color.setHex(0xCFCFCF);
                  break;

                case 'garagedeck':
                case 'maindeck':
                case 'secondarydeck':
                case 'gables':
                case 'porchroofsides':
                case 'porchrooffront':
                case 'garagefront':
                case 'sideroof':
                case 'porchgable':
                case 'glass':
                  applyMaterialProperties(mesh, meshName);
                  break;
              }
            }
          });

          scene.add(model);
          callBack?.();
        });
    });
}

function applyMaterialProperties(mesh, meshName) {
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  mats.forEach(mat => {
    mat.visible = true;
    mat.shininess = 0;
    mat.castShadow = false;
    mat.receiveShadow = false;

    if (mat.map) {
      mat.map.colorSpace = 'srgb';
      mat.map.wrapS = THREE.RepeatWrapping;
      mat.map.wrapT = THREE.RepeatWrapping;
      mat.needsUpdate = true;

      switch (meshName) {
        case 'ridge':
          mat.color.setHex(0xFFEEDD)
          mat.map = averageTextureForRidge(mat.map, 28, 28);
          break;
        case 'garagedeck':
        case 'maindeck':
        case 'secondarydeck':
        case 'gables':
        case 'porchroofsides':
        case 'porchrooffront':
        case 'garagefront':
        case 'sideroof':
        case 'porchgable':
          mat.color.setHex(0xFFEEDD)
          break;
        case 'glass':
          mat.color.setHex(0xEEEEEE);
          mat.shininess = 100;
          break;
      }
    }
  });
}

function updateSizeBasedOnContainer(targetContainer) {
  const { width, height } = targetContainer.getBoundingClientRect();
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setSize(width, height);
}

function setTextureLoading(isLoading) {
  if (isLoading) {
    loadingOverlay?.style?.setProperty('display', 'unset');
    return;
  }
  render();
  loadingOverlay?.style?.setProperty('display', 'none');
}

function beginTextureLoadBatch() {
  activeTextureRequestId += 1;
  textureLoadPendingCount = 0;
  setTextureLoading(true);
  return activeTextureRequestId;
}

function registerTextureLoad(requestId) {
  if (requestId !== activeTextureRequestId) {
    return () => {};
  }

  textureLoadPendingCount += 1;
  let finished = false;

  return () => {
    if (finished) return;
    finished = true;

    if (requestId !== activeTextureRequestId) {
      return;
    }

    textureLoadPendingCount = Math.max(0, textureLoadPendingCount - 1);

    if (textureLoadPendingCount === 0) {
      setTextureLoading(false);
    }
  };
}

export function applyTimeValue(time) {
  if (!isInitialized()) return;

  const nsin = Math.pow(Math.sin(time), 4);
  const ncos = Math.cos(time);

  dirLight.position.set(-1500 * nsin, 2000 * nsin, 2000 * ncos);

  const uniforms = sky.material.uniforms;
  uniforms['sunPosition'].value.copy(dirLight.position);

  const elevation = 90 - Math.acos(dirLight.position.y / dirLight.position.length()) * 180 / Math.PI;
  const azimuth = (180 + Math.atan2(dirLight.position.x, dirLight.position.z) * 180 / Math.PI) % 360;

  const phi = THREE.MathUtils.degToRad(90 - elevation);
  const theta = THREE.MathUtils.degToRad(azimuth);

  sun.setFromSphericalCoords(1, phi, theta);

  uniforms['turbidity'].value = nsin;
  uniforms['rayleigh'].value = 0.2 / nsin;

  const f = nsin > 0.2 ? 1 : nsin < 0.2 && nsin > 0.0 ? nsin / 0.2 : 1;

  hemiLight.intensity = 1.5 * nsin + f;
  dirLight.intensity = 3 * nsin + f;

  requestRender();
}

function applyTexture(mesh, texture = null, hasCustomRidgeTexture = false) {
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  const meshName = (mesh.name.split('_')[0] || mesh.name).toLowerCase();

  mats.forEach(mat => {
    if (mat.map && mat.map !== texture && typeof mat.map.dispose === 'function') {
      mat.map.dispose();
    }

    mat.map = texture;

    mat.color.setHex(0xFFEEDD);
    mat.map.colorSpace = 'srgb';
    mat.map.wrapS = THREE.RepeatWrapping;
    mat.map.wrapT = THREE.RepeatWrapping;
    mat.needsUpdate = true;

    switch (meshName) {
      case 'maindeck':
        mat.map.repeat.set(2.037, 1);
        break;
      case 'sideroof':
        mat.map.repeat.set(.3, .55);
        mat.map.offset.set(0.277, .205);
        mat.map.anisotropy = 16;
        break;
      case 'porchgable':
        mat.map.repeat.set(-.3, .55);
        mat.map.offset.set(0.105, .707);
        break;
      case 'gables':
        mat.map.repeat.set(.3, .55);
        mat.map.offset.set(0.225, 0.58);
        break;
      case 'garagedeck':
        mat.map.repeat.set(.37, .57);
        mat.map.offset.set(0.295, .6);
        break;
      case 'garagefront':
        mat.map.repeat.set(.3, .6);
        mat.map.offset.set(0, .767);
        break;
      case 'porchroofsides':
      case 'porchrooffront':
        mat.map.repeat.set(.4, .7);
        mat.map.offset.set(.6, 0.626);
        mat.map.anisotropy = 16;
        break;
      case 'secondarydeck':
        mat.map.repeat.set(.4, .5);
        mat.map.offset.set(-.047, .846);
        break;
      case 'ridge':
        if (!hasCustomRidgeTexture) {
          mat.map = averageTextureForRidge(mat.map, 28, 28);
        }
        break;
    }

  });
}

function averageTextureForRidge(texture, newWidth, newHeight) {
  const renderTarget = new THREE.WebGLRenderTarget(newWidth, newHeight);
  const material = new THREE.MeshBasicMaterial({ map: texture });
  const plane = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  const sceneRTT = new THREE.Scene();
  sceneRTT.add(plane);
  const cameraRTT = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  renderer.setRenderTarget(renderTarget);
  renderer.render(sceneRTT, cameraRTT);
  renderer.setRenderTarget(null);
  material.dispose();
  plane.geometry.dispose();
  texture.dispose();
  return renderTarget.texture;
}

function applyLatestTileImage() {
  if (!model || !latestTileImage) return;

  const { tileImageUrl, shingleLineSlug, shingleColorUid } = latestTileImage;
  // find h_4650 and replace with h_1550, graininess issue
  const updatedUrl = tileImageUrl ? tileImageUrl.replace(/h_\d+/, 'h_1550') : null;

  if (!updatedUrl) {
    activeTextureRequestId += 1;
    textureLoadPendingCount = 0;
    setTextureLoading(false);
    return;
  }

  const requestId = beginTextureLoadBatch();
  const targetModel = model;
  let hasLoadRequests = false;

  targetModel.traverse(mesh => {
    if (!(mesh instanceof THREE.Mesh)) return;
    const meshName = mesh.name.split('_')[0] || mesh.name;

    if (
      [
        'garagedeck',
        'maindeck',
        'secondarydeck',
        'gables',
        'porchroofsides',
        'porchrooffront',
        'garagefront',
        'sideroof',
        'porchgable',
      ].includes(meshName)
    ) {
      hasLoadRequests = true;
      const finish = registerTextureLoad(requestId);
      loader.load(
        updatedUrl,
        texture => {
          if (requestId !== activeTextureRequestId || targetModel !== model) {
            texture.dispose();
            return;
          }
          applyTexture(mesh, texture);
          finish();
        },
        undefined,
        error => {
          console.error('Failed to load shingle texture:', updatedUrl, error);
          finish();
        }
      );
    } else if (meshName === 'ridge') {
      const ridgeFilename = Object.prototype.hasOwnProperty.call(DESIGNER_RIDGE_TEXTURE_FILES, shingleColorUid)
        ? DESIGNER_RIDGE_TEXTURE_FILES[shingleColorUid]
        : null;
      const hasCustomRidgeTexture = shingleLineSlug === 'trudefinition-duration-designer' && Boolean(ridgeFilename);
      const ridgeUrl = hasCustomRidgeTexture ? `${LOCAL_TEXTURE_PATH}${ridgeFilename}?v=5` : updatedUrl;

      // A failed request must not leave the previously selected color on the ridge.
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      materials.forEach(material => {
        material.map?.dispose();
        Object.assign(material, { map: null, needsUpdate: true });
      });

      hasLoadRequests = true;
      const finish = registerTextureLoad(requestId);
      const loadRidgeTexture = (url, isCustomTexture) => {
        loader.load(
          url,
          texture => {
            if (requestId !== activeTextureRequestId || targetModel !== model) {
              texture.dispose();
              return;
            }
            applyTexture(mesh, texture, isCustomTexture);
            finish();
          },
          undefined,
          error => {
            if (requestId !== activeTextureRequestId || targetModel !== model) return;
            console.error('Failed to load ridge texture:', url, error);
            if (isCustomTexture) {
              loadRidgeTexture(updatedUrl, false);
            } else {
              finish();
            }
          }
        );
      };
      loadRidgeTexture(ridgeUrl, hasCustomRidgeTexture);
    }
  });

  if (!hasLoadRequests) {
    setTextureLoading(false);
  }

  render();
}

export const applyTileImage = (tileImageUrl, shingleLineSlug, shingleColorUid) => {
  latestTileImage = { tileImageUrl, shingleLineSlug, shingleColorUid };
  applyLatestTileImage();
};

export const initVisualizer = (
  targetContainer,
  targetLoadingOverlay,
  tileImageUrl,
  shingleLineSlug,
  shingleColorUid
) => {
  if (!hasWebGL2Support()) {
    targetLoadingOverlay?.style?.setProperty('display', 'none');
    return null;
  }

  activeVisualizerId += 1;
  const visualizerId = activeVisualizerId;
  loadingOverlay = targetLoadingOverlay;
  latestTileImage = { tileImageUrl, shingleLineSlug, shingleColorUid };
  setupRenderer(targetContainer);
  setupScene();
  setupCamera();
  updateSizeBasedOnContainer(targetContainer);
  setupLights();
  setupSky();
  loadModels(applyLatestTileImage, visualizerId);

  const resizeObserver = new ResizeObserver(() => {
    if (visualizerId !== activeVisualizerId) return;
    updateSizeBasedOnContainer(targetContainer);
    render();
  });
  resizeObserver.observe(targetContainer);

  const initializedRenderer = renderer;
  const initializedScene = scene;

  return () => {
    resizeObserver.disconnect();

    if (renderFrameId !== null) {
      window.cancelAnimationFrame?.(renderFrameId);
      renderFrameId = null;
    }

    disposeScene(initializedScene);
    initializedRenderer.dispose();
    initializedRenderer.forceContextLoss();
    if (initializedRenderer.domElement.parentNode === targetContainer) {
      targetContainer.removeChild(initializedRenderer.domElement);
    }

    if (visualizerId === activeVisualizerId) {
      activeVisualizerId += 1;
      activeTextureRequestId += 1;
      textureLoadPendingCount = 0;
      renderer = null;
      scene = null;
      camera = null;
      dirLight = null;
      hemiLight = null;
      sky = null;
      sun = null;
      model = null;
      loadingOverlay = null;
      latestTileImage = null;
    }
  };
};
